import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { FormattedMessage } from 'react-intl';
import { batch, useDispatch } from 'react-redux';
import { Alert } from '@material-ui/lab';
import { Box, Typography } from '@material-ui/core';

import { Button, DialogComponent } from 'common/components';
import CheckingSavingsStep from './steps/checkingSavings/CheckingSavingsStep';
import CreditCardPaymentStep from './steps/creditCard/CreditCardPaymentStep';
import OnAccountStep from './steps/onAccount/onAccountStep';
import SelectPaymentTypeStep from './steps/SelectPaymentTypeStep';

import { CustomTheme } from 'common/ui/interfaces';
import { IInvoiceDetailsImt, PaymentsType } from 'common/interfaces/invoices';

import { PaymentsTypeLabels } from 'modules/pos-settings/constants/paymentMethods';

import messages from 'common/components/InvoiceOperating/messages';

import { ReactComponent as BackArrowIcon } from 'img/arrow-circle-left.svg';

import { formatPrice } from 'common/utils';

import * as selectors from 'memberPortalModules/MemberProfile/state/invoice/selectors';
import * as actions from 'memberPortalModules/MemberProfile/state/invoice/actions';
import { parseErrorArgs } from 'common/utils/http';
import { PMethodSteps, PMethodStepsTitles } from 'common/components/InvoiceOperating/constants';
import { FormattedMessageByCode, ServerError } from 'common/errors/serverErrors';
import useRootSelector from 'common/hooks/useRootSelector';
import OtherTypePaymentStep from './steps/gift/OtherTypePaymentStep';
import { selectMemberProfile } from 'memberPortalModules/MemberProfile/state/profile/selectors';

interface IInvoicePaymentWizardProps {
  isOpen: boolean;
  leftToPay: number;
  memberId?: number;
  registerId: string;
  onClose: () => void;
}

const useStyles = makeStyles((theme: CustomTheme) => ({
  leftToPayBlock: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  leftToPayPrice: {
    fontWeight: theme.typography.fontWeightMedium as number,
  },
  backButton: {
    margin: theme.spacing(0, 1, 0, 0),
  },
  backIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
  },
  error: {
    marginBottom: theme.spacing(1.5),
  },
}));

const InvoicePaymentWizard: React.FC<IInvoicePaymentWizardProps> = (
  props: IInvoicePaymentWizardProps,
): JSX.Element => {
  const classes = useStyles(props);
  const { isOpen, leftToPay, memberId, onClose, registerId } = props;
  const [stepsStack, setStepsStack] = useState<PMethodSteps[]>([PMethodSteps.SelectMethodStep]);
  const [selectedType, setSelectedType] = useState<PaymentsType | null>(null);
  const dispatch = useDispatch();

  const paymentMethods = useRootSelector(selectors.selectAvailablePaymentMethods);
  const isPaymentMethodsLoading = useRootSelector(selectors.selectAvailablePaymentMethodsLoading);
  const paymentError = useRootSelector(selectors.selectUpdatedInvoiceError);
  const memberInvoice: IInvoiceDetailsImt = useRootSelector(selectors.selectMemberInvoice);
  const selectedCustomerBalanceLoading = useRootSelector(selectors.selectCustomerBalanceLoading);
  const selectedCustomerBalance = useRootSelector(selectors.selectCustomerBalance);
  const memberProfile = useRootSelector(selectMemberProfile);
  const memberHomeClubId = memberProfile?.getIn(['homeClub', 'id']);

  const isOnHandExceededServerError = paymentError
    ?.toJS()
    .codes.find(code => code === ServerError.INVENTORY_CLUB_ON_HAND_EXCEEDED);
  const currentStep = useMemo(() => stepsStack[stepsStack.length - 1], [stepsStack]);
  const paymentTypeId = useMemo(() => {
    const pMethod = paymentMethods.find(method => method.get('type') === selectedType);
    if (pMethod) {
      return pMethod.get('id');
    }

    return null;
  }, [paymentMethods, selectedType]);

  const handleSelectStep = useCallback((step: PMethodSteps, type: PaymentsType): void => {
    setStepsStack(prevStack => {
      if (prevStack.includes(step)) return prevStack;
      return [...prevStack, step];
    });

    setSelectedType(type);
  }, []);

  const handleGoBack = useCallback(() => {
    const prevStep = stepsStack[stepsStack.length - 2];

    setStepsStack(prevSteps => {
      prevSteps.pop();
      return [...prevSteps];
    });

    if (!prevStep || prevStep === PMethodSteps.SelectMethodStep) {
      setSelectedType(null);
    }
  }, [stepsStack]);

  useEffect(() => {
    if (!isOpen) {
      setStepsStack([PMethodSteps.SelectMethodStep]);
      setSelectedType(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (memberId && isOpen) {
      dispatch(actions.fetchCustomerBalanceThunk(memberId));
    }
  }, [memberId, dispatch, isOpen]);

  useEffect(() => {
    if (isOnHandExceededServerError && !isOpen) {
      dispatch(actions.updateInvoiceWithSyncErrorAction(null));
    }
  }, [dispatch, isOnHandExceededServerError, isOpen, memberInvoice]);

  useEffect(() => {
    if (isOpen) {
      dispatch(actions.fetchAvailablePaymentMethods(registerId));
    }

    return () => {
      batch(() => {
        dispatch(actions.resetAvailablePaymentMethodsList());
        dispatch(actions.resetCustomerBalance());
      });
    };
  }, [dispatch, isOpen, registerId]);

  const Title: JSX.Element = useMemo(
    () => (
      <>
        {currentStep !== PMethodSteps.SelectMethodStep && (
          <Button
            size="small"
            className={classes.backButton}
            startIcon={<BackArrowIcon className={classes.backIcon} />}
            onClick={handleGoBack}
          />
        )}

        {!selectedType && <FormattedMessage {...PMethodStepsTitles.message(currentStep)} />}

        {!!selectedType && PaymentsTypeLabels[selectedType]}
      </>
    ),
    [classes, currentStep, handleGoBack, selectedType],
  );

  const LeftToPay: JSX.Element = useMemo(
    () => (
      <Box className={classes.leftToPayBlock}>
        <Typography component="span">
          <FormattedMessage {...messages.leftToPay} />
        </Typography>
        <Typography component="span" className={classes.leftToPayPrice}>
          {formatPrice(leftToPay)}
        </Typography>
      </Box>
    ),
    [classes.leftToPayBlock, classes.leftToPayPrice, leftToPay],
  );

  const SubHeader = useMemo(() => {
    const errorCode = paymentError?.getIn(['codes', 0]);
    const errorArgs = paymentError?.get('args')?.toJS();
    const errorValues = errorArgs && parseErrorArgs(errorArgs);
    return (
      <Box mt={2} p={0}>
        {paymentError && (
          <Alert className={classes.error} severity="error">
            {FormattedMessageByCode(errorCode, errorValues) || paymentError.get('message')}
          </Alert>
        )}
        {LeftToPay}
      </Box>
    );
  }, [LeftToPay, classes.error, paymentError]);

  const balance = selectedCustomerBalance?.get('balance') || 0;

  return (
    <DialogComponent
      size="sm"
      isOpen={isOpen}
      title={Title}
      closeCross
      subHeader={SubHeader}
      hasCustomFooter
      supressBottomShadow
      footer={undefined}
      onClose={onClose}
    >
      {currentStep === PMethodSteps.SelectMethodStep && (
        <SelectPaymentTypeStep
          paymentMethodsList={paymentMethods}
          isLoading={isPaymentMethodsLoading || selectedCustomerBalanceLoading}
          balance={balance}
          onSelectStep={handleSelectStep}
          memberId={Number(memberId)}
          clubId={memberHomeClubId}
        />
      )}

      {currentStep === PMethodSteps.CreditCardPayment && (
        <CreditCardPaymentStep
          memberId={memberId}
          leftToPay={leftToPay}
          creditCardType={selectedType}
          paymentTypeId={paymentTypeId}
          onClose={onClose}
        />
      )}

      {currentStep === PMethodSteps.CheckingSavings && (
        <CheckingSavingsStep
          paymentTypeId={paymentTypeId}
          leftToPay={leftToPay}
          onClose={onClose}
          memberId={memberId}
        />
      )}

      {currentStep === PMethodSteps.OnAccount && (
        <OnAccountStep
          leftToPay={leftToPay}
          balance={balance}
          paymentTypeId={paymentTypeId}
          onClose={onClose}
        />
      )}
      {currentStep === PMethodSteps.OtherTypePayment && memberHomeClubId && (
        <OtherTypePaymentStep leftToPay={leftToPay} onClose={onClose} clubId={memberHomeClubId} />
      )}
    </DialogComponent>
  );
};

export default InvoicePaymentWizard;
